import Button from "../../components/base/Button";
import { useState } from "react";
import {
  useSEO,
  generateProductSchema,
  generateWebPageSchema,
} from "../../utils/seo";

export default function TeuPedido() {
  const [isCalendlyModalOpen, setIsCalendlyModalOpen] = useState(false);
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);

  useSEO({
    title: "TeuPedido - Gestão completa de pedidos e logística | Moveideias",
    description:
      "Plataforma completa de gestão de pedidos que integra OMS e TMS. Rastreamento, controle e visibilidade total do ciclo logístico desde a entrada até a entrega final.",
    keywords:
      "TeuPedido, gestão de pedidos, OMS, TMS, rastreamento, logística, e-commerce, delivery, varejo",
    canonical: "/teupedido",
    schema: {
      "@context": "https://schema.org",
      "@graph": [
        generateProductSchema({
          name: "TeuPedido",
          description:
            "Plataforma completa de gestão de pedidos que integra as funcionalidades de OMS e TMS, centralizando e automatizando todo o processo logístico.",
          url: "/teupedido",
        }),
        generateWebPageSchema({
          name: "TeuPedido - Gestão Completa de Pedidos",
          description:
            "Plataforma completa de gestão de pedidos com visibilidade total do ciclo logístico.",
          url: "/teupedido",
        }),
      ],
    },
  });

  const openCalendlyModal = () => {
    setIsCalendlyModalOpen(true);
  };

  const closeCalendlyModal = () => {
    setIsCalendlyModalOpen(false);
  };

  const openContactModal = () => {
    setIsContactModalOpen(true);
  };

  const closeContactModal = () => {
    setIsContactModalOpen(false);
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section
        className="relative min-h-screen flex items-center justify-center bg-cover bg-center"
        style={{
          backgroundImage: `linear-gradient(rgba(90, 106, 176, 0.6), rgba(90, 106, 176, 0.6)), url('/images/products/teupedido/hero-background.jpeg')`,
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center w-full">
          <div className="max-w-4xl mx-auto">
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-6 leading-tight">
              TeuPedido: visibilidade total do ciclo logístico
            </h1>
            <p className="text-xl md:text-2xl text-white/90 mb-8 leading-relaxed">
              Rastreamento, gestão e controle completo desde a entrada do pedido
              até a entrega no cliente final.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                size="lg"
                className="bg-[#f28c1a] text-white hover:bg-[#cc7013] transition-colors duration-200 whitespace-nowrap cursor-pointer"
                onClick={openCalendlyModal}
              >
                Solicitar demonstração
              </Button>
              <Button
                size="lg"
                className="bg-[#f28c1a] text-white hover:bg-[#cc7013] transition-colors duration-200 whitespace-nowrap cursor-pointer"
                onClick={openContactModal}
              >
                Fale conosco
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Principais Benefícios */}
      <section
        className="py-20 bg-cover bg-center relative"
        style={{
          backgroundImage: `linear-gradient(rgba(255, 255, 255, 0.95), rgba(255, 255, 255, 0.95)), url('/images/backgrounds/teupedido-benefits-main.jpg')`,
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-[#221a32] mb-6">
              Benefícios estratégicos e operacionais
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              O TeuPedido transforma sua gestão logística com visibilidade
              completa, automação inteligente e controle total do ciclo de
              pedidos.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div
              className="bg-white/90 backdrop-blur-sm p-8 rounded-xl text-center shadow-lg border border-orange-100 bg-cover bg-center relative"
              style={{
                backgroundImage: `linear-gradient(rgba(231, 234, 246, 0.9), rgba(231, 234, 246, 0.9)), url('/images/products/teupedido/visibilidade-operacional-bg.jpg')`,
              }}
            >
              <div className="w-16 h-16 bg-[#ea5836] rounded-full flex items-center justify-center mx-auto mb-4 relative z-10">
                <i className="ri-eye-line text-2xl text-white" />
              </div>
              <h3 className="text-xl font-bold text-[#221a32] mb-3 relative z-10">
                Visibilidade operacional
              </h3>
              <p className="text-gray-600 relative z-10">
                Monitoramento completo desde a entrada do pedido até a entrega,
                com informações centralizadas em tempo real.
              </p>
            </div>

            <div
              className="bg-white/90 backdrop-blur-sm p-8 rounded-xl text-center shadow-lg border border-orange-100 bg-cover bg-center relative"
              style={{
                backgroundImage: `linear-gradient(rgba(231, 234, 246, 0.9), rgba(231, 234, 246, 0.9)), url('/images/products/teupedido/eficiencia-automacao-bg.jpg')`,
              }}
            >
              <div className="w-16 h-16 bg-[#ea5836] rounded-full flex items-center justify-center mx-auto mb-4 relative z-10">
                <i className="ri-settings-3-line text-2xl text-white" />
              </div>
              <h3 className="text-xl font-bold text-[#221a32] mb-3 relative z-10">
                Eficiência e automação
              </h3>
              <p className="text-gray-600 relative z-10">
                Redução de esforço manual e otimização de processos com
                automação inteligente.
              </p>
            </div>

            <div
              className="bg-white/90 backdrop-blur-sm p-8 rounded-xl text-center shadow-lg border border-orange-100 bg-cover bg-center relative"
              style={{
                backgroundImage: `linear-gradient(rgba(231, 234, 246, 0.9), rgba(231, 234, 246, 0.9)), url('/images/products/teupedido/comunicacao-transparencia-bg.jpg')`,
              }}
            >
              <div className="w-16 h-16 bg-[#ea5836] rounded-full flex items-center justify-center mx-auto mb-4 relative z-10">
                <i className="ri-message-3-line text-2xl text-white" />
              </div>
              <h3 className="text-xl font-bold text-[#221a32] mb-3 relative z-10">
                Comunicação e transparência
              </h3>
              <p className="text-gray-600 relative z-10">
                Compartilhamento de status atualizados e notificações
                automáticas que elevam a satisfação do cliente.
              </p>
            </div>

            <div
              className="bg-white/90 backdrop-blur-sm p-8 rounded-xl text-center shadow-lg border border-orange-100 bg-cover bg-center relative"
              style={{
                backgroundImage: `linear-gradient(rgba(231, 234, 246, 0.9), rgba(231, 234, 246, 0.9)), url('/images/products/teupedido/auditoria-de-fretes-bg.jpg')`,
              }}
            >
              <div className="w-16 h-16 bg-[#ea5836] rounded-full flex items-center justify-center mx-auto mb-4 relative z-10">
                <i className="ri-file-check-line text-2xl text-white" />
              </div>
              <h3 className="text-xl font-bold text-[#221a32] mb-3 relative z-10">
                Auditoria de fretes
              </h3>
              <p className="text-gray-600 relative z-10">
                Conferência automática de CT-es e faturas, evitando cobranças
                indevidas e melhorando a acuracidade financeira.
              </p>
            </div>

            <div
              className="bg-white/90 backdrop-blur-sm p-8 rounded-xl text-center shadow-lg border border-orange-100 bg-cover bg-center relative"
              style={{
                backgroundImage: `linear-gradient(rgba(231, 234, 246, 0.9), rgba(231, 234, 246, 0.9)), url('/images/products/teupedido/rastreamento-tempo-real-bg.jpg')`,
              }}
            >
              <div className="w-16 h-16 bg-[#ea5836] rounded-full flex items-center justify-center mx-auto mb-4 relative z-10">
                <i className="ri-map-pin-time-line text-2xl text-white" />
              </div>
              <h3 className="text-xl font-bold text-[#221a32] mb-3 relative z-10">
                Rastreamento em tempo real
              </h3>
              <p className="text-gray-600 relative z-10">
                Acompanhamento contínuo da situação do pedido e movimentação da
                carga com total transparência.
              </p>
            </div>

            <div
              className="bg-white/90 backdrop-blur-sm p-8 rounded-xl text-center shadow-lg border border-orange-100 bg-cover bg-center relative"
              style={{
                backgroundImage: `linear-gradient(rgba(231, 234, 246, 0.9), rgba(231, 234, 246, 0.9)), url('/images/products/teupedido/decisoes-baseadas-em-dados-bg.jpg')`,
              }}
            >
              <div className="w-16 h-16 bg-[#ea5836] rounded-full flex items-center justify-center mx-auto mb-4 relative z-10">
                <i className="ri-bar-chart-box-line text-2xl text-white" />
              </div>
              <h3 className="text-xl font-bold text-[#221a32] mb-3 relative z-10">
                Decisões baseadas em dados
              </h3>
              <p className="text-gray-600 relative z-10">
                Relatórios e dashboards operacionais que apoiam gestores na
                análise de performance e melhorias contínuas.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Problemas que Resolve */}
      <section
        className="py-20 bg-cover bg-center relative"
        style={{
          backgroundImage: `linear-gradient(rgba(231, 234, 246, 0.95), rgba(231, 234, 246, 0.95)), url('/images/backgrounds/teupedido-problems-solved.jpg')`,
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-[#221a32] mb-6">
              Problemas críticos que o TeuPedido resolve
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Transforme os principais desafios logísticos em vantagens
              competitivas com nossa plataforma integrada.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="bg-white p-8 rounded-xl shadow-lg border border-gray-100">
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-red-500 rounded-full flex items-center justify-center flex-shrink-0">
                  <i className="ri-close-line text-xl text-white" />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-bold text-red-600 mb-2">
                    PROBLEMA
                  </h3>
                  <p className="text-gray-700 mb-4">
                    Falta de visibilidade end-to-end do ciclo de pedidos
                  </p>
                </div>
              </div>
              <div className="flex items-start space-x-4 mt-6">
                <div className="w-12 h-12 bg-green-600 rounded-full flex items-center justify-center flex-shrink-0">
                  <i className="ri-check-line text-xl text-white" />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-bold text-green-600 mb-2">
                    SOLUÇÃO
                  </h3>
                  <p className="text-gray-700">
                    Rastreamento em tempo real do ciclo completo do pedido
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white p-8 rounded-xl shadow-lg border border-gray-100">
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-red-500 rounded-full flex items-center justify-center flex-shrink-0">
                  <i className="ri-close-line text-xl text-white" />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-bold text-red-600 mb-2">
                    PROBLEMA
                  </h3>
                  <p className="text-gray-700 mb-4">
                    Processos manuais e descentralizados
                  </p>
                </div>
              </div>
              <div className="flex items-start space-x-4 mt-6">
                <div className="w-12 h-12 bg-green-600 rounded-full flex items-center justify-center flex-shrink-0">
                  <i className="ri-check-line text-xl text-white" />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-bold text-green-600 mb-2">
                    SOLUÇÃO
                  </h3>
                  <p className="text-gray-700">
                    Centralização e automação em uma plataforma única
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white p-8 rounded-xl shadow-lg border border-gray-100">
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-red-500 rounded-full flex items-center justify-center flex-shrink-0">
                  <i className="ri-close-line text-xl text-white" />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-bold text-red-600 mb-2">
                    PROBLEMA
                  </h3>
                  <p className="text-gray-700 mb-4">
                    Comunicação ineficiente entre áreas e parceiros
                  </p>
                </div>
              </div>
              <div className="flex items-start space-x-4 mt-6">
                <div className="w-12 h-12 bg-green-600 rounded-full flex items-center justify-center flex-shrink-0">
                  <i className="ri-check-line text-xl text-white" />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-bold text-green-600 mb-2">
                    SOLUÇÃO
                  </h3>
                  <p className="text-gray-700">
                    Compartilhamento de informações e integração operacional
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white p-8 rounded-xl shadow-lg border border-gray-100">
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-red-500 rounded-full flex items-center justify-center flex-shrink-0">
                  <i className="ri-close-line text-xl text-white" />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-bold text-red-600 mb-2">
                    PROBLEMA
                  </h3>
                  <p className="text-gray-700 mb-4">
                    Auditoria de frete trabalhosa ou imprecisa
                  </p>
                </div>
              </div>
              <div className="flex items-start space-x-4 mt-6">
                <div className="w-12 h-12 bg-green-600 rounded-full flex items-center justify-center flex-shrink-0">
                  <i className="ri-check-line text-xl text-white" />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-bold text-green-600 mb-2">
                    SOLUÇÃO
                  </h3>
                  <p className="text-gray-700">
                    Conferência eletrônica automatizada de CT-es e faturas
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Impactos nas Áreas */}
      <section
        className="py-20 bg-cover bg-center relative"
        style={{
          backgroundImage: `linear-gradient(rgba(255, 255, 255, 0.95), rgba(255, 255, 255, 0.95)), url('/images/products/teupedido/impactos-areas.jpeg')`,
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-[#221a32] mb-6">
              Impactos transformadores em todas as áreas
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-white p-6 rounded-xl shadow-lg border border-gray-100">
              <div className="w-12 h-12 bg-[#ea5836] rounded-full flex items-center justify-center mb-4">
                <i className="ri-truck-line text-xl text-white" />
              </div>
              <h3 className="text-lg font-bold text-[#221a32] mb-3">
                Gestão logística
              </h3>
              <p className="text-gray-600">
                Visibilidade completa e controle das entregas
              </p>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-lg border border-gray-100">
              <div className="w-12 h-12 bg-[#ea5836] rounded-full flex items-center justify-center mb-4">
                <i className="ri-settings-line text-xl text-white" />
              </div>
              <h3 className="text-lg font-bold text-[#221a32] mb-3">
                Operações
              </h3>
              <p className="text-gray-600">
                Digitalização e automação de tarefas operacionais
              </p>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-lg border border-gray-100">
              <div className="w-12 h-12 bg-[#ea5836] rounded-full flex items-center justify-center mb-4">
                <i className="ri-money-dollar-circle-line text-xl text-white" />
              </div>
              <h3 className="text-lg font-bold text-[#221a32] mb-3">
                Financeiro
              </h3>
              <p className="text-gray-600">
                Redução de custos com frete e conferência automatizada
              </p>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-lg border border-gray-100">
              <div className="w-12 h-12 bg-[#ea5836] rounded-full flex items-center justify-center mb-4">
                <i className="ri-customer-service-2-line text-xl text-white" />
              </div>
              <h3 className="text-lg font-bold text-[#221a32] mb-3">
                Atendimento ao cliente
              </h3>
              <p className="text-gray-600">
                Atualizações em tempo real e maior transparência
              </p>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-lg border border-gray-100">
              <div className="w-12 h-12 bg-[#ea5836] rounded-full flex items-center justify-center mb-4">
                <i className="ri-line-chart-line text-xl text-white" />
              </div>
              <h3 className="text-lg font-bold text-[#221a32] mb-3">
                Tomada de decisão
              </h3>
              <p className="text-gray-600">
                Acesso a dados confiáveis e análises de performance logística
              </p>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-lg border border-gray-100">
              <div className="w-12 h-12 bg-[#ea5836] rounded-full flex items-center justify-center mb-4">
                <i className="ri-team-line text-xl text-white" />
              </div>
              <h3 className="text-lg font-bold text-[#221a32] mb-3">
                Colaboração interna
              </h3>
              <p className="text-gray-600">
                Alinhamento entre comercial, logística, SAC e demais áreas
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Funcionalidades Principais */}
      <section
        className="py-20 bg-cover bg-center relative"
        style={{
          backgroundImage: `linear-gradient(rgba(231, 234, 246, 0.95), rgba(231, 234, 246, 0.95)), url('/images/products/teupedido/funcionalidades.jpeg')`,
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-[#221a32] mb-6">
              Funcionalidades que transformam sua operação
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-white/90 backdrop-blur-sm p-8 rounded-xl text-center shadow-lg border border-orange-100">
              <div className="w-16 h-16 bg-[#ea5836] rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-shopping-cart-line text-2xl text-white" />
              </div>
              <h3 className="text-xl font-bold text-[#221a32] mb-3">
                Gestão de pedidos
              </h3>
              <p className="text-gray-600">
                Centralize todos os pedidos em uma única plataforma com controle
                total do fluxo.
              </p>
            </div>

            <div className="bg-white/90 backdrop-blur-sm p-8 rounded-xl text-center shadow-lg border border-orange-100">
              <div className="w-16 h-16 bg-[#ea5836] rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-store-line text-2xl text-white" />
              </div>
              <h3 className="text-xl font-bold text-[#221a32] mb-3">
                Multi-canal
              </h3>
              <p className="text-gray-600">
                Integre vendas de diferentes canais: loja física, e-commerce,
                marketplace.
              </p>
            </div>

            <div className="bg-white/90 backdrop-blur-sm p-8 rounded-xl text-center shadow-lg border border-orange-100">
              <div className="w-16 h-16 bg-[#ea5836] rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-notification-line text-2xl text-white" />
              </div>
              <h3 className="text-xl font-bold text-[#221a32] mb-3">
                Notificações automáticas
              </h3>
              <p className="text-gray-600">
                Mantenha clientes informados sobre o status dos pedidos
                automaticamente.
              </p>
            </div>

            <div className="bg-white/90 backdrop-blur-sm p-8 rounded-xl text-center shadow-lg border border-orange-100">
              <div className="w-16 h-16 bg-[#ea5836] rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-shield-check-line text-2xl text-white" />
              </div>
              <h3 className="text-xl font-bold text-[#221a32] mb-3">
                Gestão de transportadores
              </h3>
              <p className="text-gray-600">
                Monitore performance e confiabilidade das entregas com
                indicadores detalhados.
              </p>
            </div>

            <div className="bg-white/90 backdrop-blur-sm p-8 rounded-xl text-center shadow-lg border border-orange-100">
              <div className="w-16 h-16 bg-[#ea5836] rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-money-dollar-circle-line text-2xl text-white" />
              </div>
              <h3 className="text-xl font-bold text-[#221a32] mb-3">
                Gestão financeira
              </h3>
              <p className="text-gray-600">
                Controle de pagamentos, faturamento e conciliação financeira
                integrada.
              </p>
            </div>

            <div className="bg-white/90 backdrop-blur-sm p-8 rounded-xl text-center shadow-lg border border-orange-100">
              <div className="w-16 h-16 bg-[#ea5836] rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-history-line text-2xl text-white" />
              </div>
              <h3 className="text-xl font-bold text-[#221a32] mb-3">
                Histórico completo
              </h3>
              <p className="text-gray-600">
                Rastreabilidade total de cada etapa do pedido com registros
                detalhados.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Casos de Uso */}
      <section
        className="py-20 bg-cover bg-center relative"
        style={{
          backgroundImage: `linear-gradient(rgba(255, 255, 255, 0.95), rgba(255, 255, 255, 0.95)), url('/images/backgrounds/teupedido-usecases.jpg')`,
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-[#221a32] mb-6">
              Ideal para diversos segmentos
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-white p-8 rounded-xl shadow-lg border border-gray-100">
              <div className="w-16 h-16 bg-[#ea5836] rounded-full flex items-center justify-center mb-6">
                <i className="ri-t-shirt-line text-2xl text-white" />
              </div>
              <h3 className="text-xl font-bold text-[#221a32] mb-4">
                E-commerce
              </h3>
              <p className="text-gray-600">
                Perfeito para lojas online que precisam gerenciar grandes
                volumes de pedidos com eficiência.
              </p>
            </div>

            <div className="bg-white p-8 rounded-xl shadow-lg border border-gray-100">
              <div className="w-16 h-16 bg-[#ea5836] rounded-full flex items-center justify-center mb-6">
                <i className="ri-restaurant-line text-2xl text-white" />
              </div>
              <h3 className="text-xl font-bold text-[#221a32] mb-4">
                Food Service
              </h3>
              <p className="text-gray-600">
                Ideal para restaurantes e delivery que necessitam de agilidade
                no processamento de pedidos.
              </p>
            </div>

            <div className="bg-white p-8 rounded-xl shadow-lg border border-gray-100">
              <div className="w-16 h-16 bg-[#ea5836] rounded-full flex items-center justify-center mb-6">
                <i className="ri-building-line text-2xl text-white" />
              </div>
              <h3 className="text-xl font-bold text-[#221a32] mb-4">Varejo</h3>
              <p className="text-gray-600">
                Excelente para redes de varejo que operam em múltiplos canais de
                venda.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Final */}
      <section
        className="py-20 bg-cover bg-center relative"
        style={{
          backgroundImage: `linear-gradient(rgba(234, 88, 54, 0.9), rgba(234, 88, 54, 0.9)), url('/images/backgrounds/teupedido-cta.jpg')`,
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Pronto para revolucionar sua gestão de pedidos?
          </h2>
          <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
            Descubra como o TeuPedido pode transformar sua operação logística
            com visibilidade total e controle inteligente.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              size="lg"
              className="bg-[#f28c1a] text-white hover:bg-[#cc7013] transition-colors duration-200 whitespace-nowrap cursor-pointer"
              onClick={openCalendlyModal}
            >
              Solicitar demonstração
            </Button>
            <Button
              size="lg"
              className="bg-[#f28c1a] text-white hover:bg-[#cc7013] transition-colors duration-200 whitespace-nowrap cursor-pointer"
              onClick={openContactModal}
            >
              Fale conosco
            </Button>
          </div>
        </div>
      </section>

      {/* Calendly Modal */}
      {isCalendlyModalOpen && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          onClick={closeCalendlyModal}
        >
          <div
            className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h3 className="text-2xl font-bold text-[#221a32]">
                Agendar demonstração
              </h3>
              <button
                onClick={closeCalendlyModal}
                className="w-10 h-10 flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors duration-200 cursor-pointer"
              >
                <i className="ri-close-line text-2xl" />
              </button>
            </div>
            <div className="h-[600px]">
              <iframe
                src="https://calendly.com/move-comercial/60min"
                className="w-full h-full"
                title="Agendar demonstração"
              />
            </div>
          </div>
        </div>
      )}

      {/* Contact Modal */}
      {isContactModalOpen && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          onClick={closeContactModal}
        >
          <div
            className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h3 className="text-2xl font-bold text-[#221a32]">
                Fale conosco
              </h3>
              <button
                onClick={closeContactModal}
                className="w-10 h-10 flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors duration-200 cursor-pointer"
              >
                <i className="ri-close-line text-2xl" />
              </button>
            </div>
            <div className="h-[600px]">
              <iframe
                src="https://portal.moveideias.com/workflow/public/form/9ec92cd7-ac11-4a04-8a8a-6d71c473e303"
                width="100%"
                height="100%"
                frameBorder="0"
                title="Formulário de contato"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
